// telemetry/init.js
// Entry point for browser OpenTelemetry tracing + logging.
// Initializes immediately at startup with window.__RUNTIME_CONFIG__ defaults
// (getReplaneConfig falls back to those defaults until Replane connects). If
// Replane is unreachable or telemetry was disabled at startup, subscribes once
// to onReplaneReady so a later "enabled=true" from the dashboard can still
// bring providers online. Heavy OTel packages are dynamic-imported only when
// actually enabled — keeps them out of the main bundle.
//
// Console patch + global error capture are installed unconditionally on the
// first initTelemetry() call (lightweight — no OTel collector required) so
// local dev logs/errors are captured even when the exporter is not configured.

import { onReplaneReady, getReplaneConfig, asBool } from "../utils/replaneClient"
import { idpOrigins } from "../context/auth/runtime-config"

let _initialized = false
let _captureInstalled = false

// Console patch + global error capture — always runs regardless of OTel enabled
// state. Separated from _runSetup so it fires even when enabled=false.
const _installCapture = () => {
  if (_captureInstalled) return
  _captureInstalled = true
  const consoleCaptureEnabled = asBool(getReplaneConfig("observability", "frontend.console_capture_enabled", true))
  const windowErrorCaptureEnabled = asBool(getReplaneConfig("observability", "frontend.window_error_capture_enabled", true))
  import("./console-capture")
    .then(({ installConsolePatch, installWindowErrorCapture }) => {
      if (consoleCaptureEnabled) installConsolePatch()
      if (windowErrorCaptureEnabled) installWindowErrorCapture()
    })
    .catch((err) => console.error("[telemetry] console-capture load failed:", err))
}

const _runSetup = () => {
  if (_initialized) return

  // Fall back to window.__RUNTIME_CONFIG__ so OTel works even when Replane has no configs
  const runtimeEnabled = window.__RUNTIME_CONFIG__?.REACT_APP_OTEL_ENABLED === "true"
  const enabled = asBool(getReplaneConfig("observability", "frontend.enabled", runtimeEnabled))
  if (!enabled) return

  const runtimeEndpoint = window.__RUNTIME_CONFIG__?.REACT_APP_OTEL_EXPORTER_ENDPOINT || ""
  const runtimeCorsUrls = window.__RUNTIME_CONFIG__?.REACT_APP_OTEL_PROPAGATE_CORS_URLS || ".*localhost.*"
  const version = window.__RUNTIME_CONFIG__?.REACT_APP_VERSION ?? "0.0.0"
  const environment = window.__RUNTIME_CONFIG__?.REACT_APP_ENV ?? "unknown"

  const consoleExporter = asBool(getReplaneConfig("observability", "frontend.console_exporter", false))
  const rawExporterEndpoint = getReplaneConfig("observability", "frontend.exporter_endpoint", runtimeEndpoint)
  // A localhost collector is only reachable when the app is BROWSED from that same
  // machine. For any remote viewer (dev host, ui-demo) every trace/log batch fails
  // with a CORS/network error — constant console noise and wasted request overhead.
  const endpointIsLocalhost = /\/\/(localhost|127\.0\.0\.1)[:/]/.test(rawExporterEndpoint || "")
  const pageIsLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname)
  const exporterEndpoint = endpointIsLocalhost && !pageIsLocalhost ? "" : rawExporterEndpoint
  const propagateCorsUrls = getReplaneConfig("observability", "frontend.propagate_cors_urls", runtimeCorsUrls)

  // Never instrument an identity provider — see the ignoreUrls comment in
  // telemetry-provider.js. EVERY configured IdP is excluded, not just one: the active
  // provider can be switched at runtime (Replane auth.provider), so either may be in use.
  const ignoreUrlPatterns = idpOrigins().map((origin) => new RegExp(origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")))

  // Logs-specific config
  const logsEnabled = asBool(getReplaneConfig("observability", "frontend.logs_enabled", true))
  const logLevel = getReplaneConfig("observability", "frontend.log_level", "warn")

  // Per-tool controls — when sentry_enabled is false, skip all OTel providers
  // (traces and logs flow through collector to Sentry, so disabling here = zero Sentry calls)
  // 'inherit' or null = inherit from frontend.enabled (default: on)
  const rawSentryEnabled = getReplaneConfig("observability", "frontend.sentry_enabled", null)
  const sentryOff = rawSentryEnabled !== null && rawSentryEnabled !== "inherit" && !asBool(rawSentryEnabled)

  // _initialized is set before the async imports fire. This is intentional:
  // OTel WebTracerProvider / LoggerProvider install themselves as global singletons
  // via register() / setGlobalLoggerProvider(). Resetting the flag on import
  // failure and retrying would register a second provider while the first's
  // BatchSpanProcessor queue keeps draining — no clean shutdown without keeping
  // provider references and calling await provider.shutdown(). .catch() handlers
  // below prevent the rejected imports from becoming unhandled rejections.
  _initialized = true

  // Traces — dynamic import keeps OTel trace packages out of the main bundle
  if (!sentryOff) {
    import("./telemetry-provider")
      .then(({ setupTelemetry }) => {
        setupTelemetry({ consoleExporter, exporterEndpoint, propagateCorsUrls, ignoreUrlPatterns, version })
      })
      .catch((err) => console.error("[telemetry] telemetry-provider load failed:", err))
  }

  // Logs — dynamic import keeps sdk-logs + OTLP log exporter out of the main bundle
  if (!sentryOff && logsEnabled && exporterEndpoint) {
    import("./logging-provider")
      .then(({ setupLogging }) => {
        setupLogging({ exporterEndpoint, consoleExporter, version, environment, logLevel })
      })
      .catch((err) => console.error("[telemetry] logging-provider load failed:", err))
  }

  // Fetch correlation — wraps window.fetch to capture x-request-id from responses.
  // Called after FetchInstrumentation registers so we layer on top of the instrumented fetch.
  if (!sentryOff) {
    import("./request-context")
      .then(({ installFetchCorrelation }) => {
        installFetchCorrelation()
      })
      .catch((err) => console.error("[telemetry] request-context load failed:", err))
  }
}

const initTelemetry = () => {
  // Console capture runs regardless of OTel enabled state — lightweight, no collector needed.
  _installCapture()

  // Run OTel provider setup immediately — getReplaneConfig falls back to runtime env
  // defaults if Replane isn't connected yet, so this works in every Replane state.
  _runSetup()

  // If the first attempt skipped (env-default enabled=false), give Replane a
  // chance to flip it on once it connects. _runSetup is idempotent via the
  // _initialized flag, so re-entry after a successful init is a no-op.
  if (!_initialized) {
    const unsubscribe = onReplaneReady(() => {
      _runSetup()
      if (_initialized) unsubscribe()
    })
  }
}

export default initTelemetry
